import { FC, useCallback } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { IFormProps } from '@/types/form';
import { Grid, Stack, Typography } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import PasswordField from '@/components/common/PasswordField';
import StyledLoadingButton from '@/components/common/StyledLoadingButton';

type PasswordValidation = {
    minLength: boolean;
    hasUppercase: boolean;
    hasNumber: boolean;
    hasSpecialChar: boolean;
};

type PasswordStrength =
    | 'muy_debil'
    | 'debil'
    | 'aceptable'
    | 'segura'
    | 'muy_segura';

const passwordStrengthDic: Record<PasswordStrength, string> = {
    muy_debil: 'Muy débil',
    debil: 'Débil',
    aceptable: 'Aceptable',
    segura: 'Segura',
    muy_segura: 'Muy segura'
};

type PasswordValidationResult = {
    rules: PasswordValidation;
    score: number;
    strength: PasswordStrength;
};

export type PasswordEditFormFields = {
    oldPassword: string;
    newPassword: string;
    repeatPassword: string;
};

const emptyProfileValues: PasswordEditFormFields = {
    newPassword: '',
    oldPassword: '',
    repeatPassword: '',
};

const helpTexts: { [k in keyof PasswordEditFormFields]?: string } = {
    newPassword:
        'Admite de forma obligatoria letras, números y caracteres especiales. Debe poseer mínimo 8 caracteres. No debe ser igual a la contraseña actual.',
    oldPassword:
        'Admite de forma obligatoria letras, números y caracteres especiales. Debe poseer mínimo 8 caracteres.',
    repeatPassword:
        'El valor debe ser exactamente igual al valor colocado en el campo contraseña nueva',
};

type PasswordEditFormProps = IFormProps<PasswordEditFormFields> & {
    wrongCredentials?: boolean
    loadingUpdate?: boolean;
    onCancel?: () => void;
};

const PasswordEditForm: FC<PasswordEditFormProps> = ({
    wrongCredentials = false,
    formId,
    submitAction,
    loadingUpdate,
    onCancel
}) => {

    const validationSchema = yup.object().shape({
        newPassword: yup.string()
            .required('La contraseña es un campo obligatorio'),
        oldPassword: yup.string()
            .min(8, 'La contraseña debe tener más de 8 caracteres')
            .matches(/[a-zA-Z]/, 'La contraseña debe contener al menos una letra')
            .matches(/[0-9]/, 'La contraseña debe contener al menos un número')
            .matches(/[!@#$]/, 'La contraseña debe contener al menos un carácter especial (!@#$)')
            .required('La contraseña es un campo obligatorio'),
        repeatPassword: yup.string()
            .min(8, 'La contraseña debe tener más de 8 caracteres')
            .matches(/[a-zA-Z]/, 'La contraseña debe contener al menos una letra')
            .matches(/[0-9]/, 'La contraseña debe contener al menos un número')
            .matches(/[!@#$]/, 'La contraseña debe contener al menos un carácter especial (!@#$)')
            .required('La contraseña es un campo obligatorio')
            .oneOf([yup.ref('newPassword')],
                'La contraseña debe ser igual a la anterior'
            ),
    });

    const {
        isValid,
        values,
        touched,
        errors,
        dirty,
        handleChange,
        handleBlur,
        handleSubmit,
        resetForm,
        setErrors,
    } = useFormik<PasswordEditFormFields>({
        initialValues: emptyProfileValues,
        validationSchema,
        validateOnMount: true,
        validateOnBlur: true,
        validateOnChange: true,
        onSubmit: (values) => {
            submitAction(values, resetForm);
        },
    });

    const handleClean = useCallback(() => {
        onCancel ? onCancel() : window.history.back();
        resetForm();
        setErrors({});
    }, []);

    const validatePassword = (password: string): PasswordValidationResult => {
        const rules: PasswordValidation = {
            minLength: password.length >= 8,
            hasUppercase: /[A-Z]/.test(password),
            hasNumber: /\d/.test(password),
            hasSpecialChar: /[!@#$]/.test(password),
        };

        const score =
            Number(rules.minLength) +
            Number(rules.hasUppercase) +
            Number(rules.hasNumber) +
            Number(rules.hasSpecialChar);

        const strengthMap: Record<number, PasswordStrength> = {
            0: 'muy_debil',
            1: 'muy_debil',
            2: 'debil',
            3: 'aceptable',
            4: 'segura',
        };

        return {
            rules,
            score,
            strength: strengthMap[score],
        };
    }

    const passwordValidation = validatePassword(values.newPassword);
    const isPasswordStrong = passwordValidation.score === 4;

    const getNewPasswordHelperText = () => {
        if (touched.newPassword && errors.newPassword) {
            return (<span>{errors.newPassword}</span>);
        }

        if (values.newPassword && passwordValidation) {
            return (
                <Typography display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                    <Typography variant='caption' sx={{ color: 'grey.900', fontWeight: 600, textTransform: 'uppercase' }}>Seguridad de la contraseña</Typography>
                    <Typography variant='caption'>{passwordStrengthDic[passwordValidation.strength]}</Typography>
                </Typography>
            )
        }

        return (<span>{helpTexts.newPassword}</span>);
    };

    const RequirementIcon = ({ valid }: { valid: boolean }) =>
        valid ? (
            <CheckCircleOutlineOutlinedIcon sx={{ fontSize: 14 }} />
        ) : (
            <CancelOutlinedIcon sx={{ fontSize: 14 }} />
        );

    return (
        <form
            id={formId}
            onSubmit={handleSubmit}
        >
            <Grid container padding={2} borderRadius={2} display={'flex'} justifyContent={'space-between'} gap={0} sx={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.15)' }}>
                <Grid item container xs={6}>
                    <Stack gap={2} width={'100%'}>
                        <PasswordField
                            required
                            size="small"
                            fullWidth
                            name="oldPassword"
                            label="Contraseña actual"
                            value={values.oldPassword}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={!!errors.oldPassword && !!touched.oldPassword || wrongCredentials}
                            helperText={
                                wrongCredentials ? 'Contraseña actual incorrecta' :
                                    touched.oldPassword && errors.oldPassword
                                        ? errors.oldPassword
                                        : helpTexts.oldPassword
                            }
                        />
                        <PasswordField
                            required
                            size="small"
                            fullWidth
                            name="newPassword"
                            label="Nueva contraseña"
                            value={values.newPassword}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.newPassword && !isPasswordStrong}
                            helperText={getNewPasswordHelperText()}
                        />
                        <PasswordField
                            required
                            size="small"
                            fullWidth
                            name="repeatPassword"
                            label="Repetir contraseña"
                            value={values.repeatPassword}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={!!errors.repeatPassword && !!touched.repeatPassword}
                            helperText={
                                touched.repeatPassword && errors.repeatPassword
                                    ? errors.repeatPassword
                                    : helpTexts.repeatPassword
                            }
                        />
                    </Stack>
                </Grid>
                <Grid item container padding={2} xs={5} sx={{ backgroundColor: 'grey.50', borderRadius: 2, boxShadow: '0 1px 3px rgba(0, 0, 0, 0.15)' }}>
                    <Stack width={'100%'} justifyContent={'space-between'}>
                        <Stack gap={1}>
                            <Typography variant='body2' sx={{ textTransform: 'uppercase', color: 'grey.900', fontWeight: 600 }}>
                                Requisitos de seguridad
                            </Typography>
                            <Typography
                                variant='caption'
                                sx={{
                                    color: passwordValidation.rules.minLength ? 'success.dark' : 'grey.600',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}>
                                <RequirementIcon valid={passwordValidation.rules.minLength} /> Mínimo 8 caracteres
                            </Typography>
                            <Typography
                                variant='caption'
                                sx={{
                                    color: passwordValidation.rules.hasUppercase ? 'success.dark' : 'grey.600',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}>
                                <RequirementIcon valid={passwordValidation.rules.hasUppercase} /> Al menos una mayúscula
                            </Typography>
                            <Typography
                                variant='caption'
                                sx={{
                                    color: passwordValidation.rules.hasNumber ? 'success.dark' : 'grey.600',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}>
                                <RequirementIcon valid={passwordValidation.rules.hasNumber} /> Al menos un número
                            </Typography>
                            <Typography
                                variant='caption'
                                sx={{
                                    color: passwordValidation.rules.hasSpecialChar ? 'success.dark' : 'grey.600',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}>
                                <RequirementIcon valid={passwordValidation.rules.hasSpecialChar} /> Un carácter especial (!@#$)
                            </Typography>
                        </Stack>
                        <Grid sx={{ backgroundColor: 'background.default', borderRadius: 2, padding: 1, boxShadow: '0 1px 3px rgba(0, 0, 0, 0.15)' }}>
                            <Typography component={'i'} variant='caption' sx={{ color: 'grey.600', fontSize: '11px', lineHeight: 1.2 }}>
                                "Una contraseña robusta protege tu identidad digital y previene accesos no autorizados a tus datos de empresa."
                            </Typography>
                        </Grid>
                    </Stack>
                </Grid>
            </Grid>
            <Grid container display={'flex'} justifyContent={'flex-end'} gap={2}>
                <Stack
                    direction="row"
                    justifyContent="center"
                    sx={{ mt: { md: 2.5, xs: 0 } }}
                    gap={1}
                >
                    <StyledLoadingButton
                        loading={loadingUpdate}
                        disabled={!isValid || loadingUpdate || !dirty || !isPasswordStrong}
                        type="submit"
                    >
                        <Grid container gap={1} width={150} display={'flex'} justifyContent={'center'} alignItems={'center'}>
                            {
                                !loadingUpdate && (
                                    <>
                                        <SaveIcon fontSize='small' />
                                        Guardar cambios
                                    </>
                                )
                            }
                        </Grid>
                    </StyledLoadingButton>
                    <StyledLoadingButton
                        variant="outlined"
                        onClick={handleClean}
                    >
                        Cancelar
                    </StyledLoadingButton>
                </Stack>
            </Grid>
        </form>
    );
};

export default PasswordEditForm;
